import { watch, onUnmounted } from 'vue';

/**
 * Size + position a fixed element to the *visual* viewport while a flag is true
 * — used to keep the drawer (and its bottom-anchored composer) inside the area
 * that's actually visible on mobile, above the on-screen keyboard.
 *
 *   useVisualViewport(drawerRef, isOpen);
 *
 * `100dvh` handles the browser's retractable toolbar but NOT the on-screen
 * keyboard. On iOS the keyboard shrinks the visual viewport AND scrolls the
 * page to reveal the focused input, so a `position: fixed` element (pinned to
 * the layout viewport) ends up above the visible area — its header slides off
 * the top. The VisualViewport API reports both the shrink (`height`) and the
 * scroll (`offsetTop`), so we pin the element's `top` to `offsetTop` and its
 * `height` to `height`, parking it exactly over the visible region.
 *
 * When inactive we clear the inline styles so the element falls back to its CSS
 * height (`100dvh`). Gated to mobile — desktop has no toolbar/keyboard overlap.
 */
const DEFAULT_QUERY = '(max-width: 640px)';

/**
 * Position + size (CSS px) for the drawer so it covers the visible region of
 * the visual viewport: pinned to the visible top (`offsetTop`), as tall as the
 * visible height. On iOS `offsetTop` is how far the page scrolled to reveal the
 * focused input, so matching it cancels that shift and re-aligns the drawer.
 *
 * @param {{ height: number, offsetTop: number }} vv  visual viewport
 * @returns {{ top: string, height: string }} CSS values, e.g. `{ top: '334px', height: '339px' }`
 */
export function computeDrawerStyle(vv) {
    return { top: `${vv.offsetTop}px`, height: `${vv.height}px` };
}

/**
 * @param {CSSStyleDeclaration} style
 * @param {{ height: number, offsetTop: number }} vv
 */
export function applyViewportSize(style, vv) {
    const { top, height } = computeDrawerStyle(vv);
    style.top = top;
    style.height = height;
}

/** @param {CSSStyleDeclaration} style */
export function clearViewportSize(style) {
    style.height = '';
    style.top = '';
}

export function useVisualViewport(elRef, active, mediaQuery = DEFAULT_QUERY) {
    if (typeof window === 'undefined' || !window.visualViewport) {
        return;
    }
    const vv = window.visualViewport;

    function matches() {
        return !mediaQuery || window.matchMedia(mediaQuery).matches;
    }

    function sync() {
        if (elRef.value && active.value && matches()) {
            applyViewportSize(elRef.value.style, vv);
        }
    }

    function reset() {
        if (elRef.value) {
            clearViewportSize(elRef.value.style);
        }
    }

    function start() {
        vv.addEventListener('resize', sync);
        vv.addEventListener('scroll', sync);
    }

    function stop() {
        vv.removeEventListener('resize', sync);
        vv.removeEventListener('scroll', sync);
    }

    watch(active, (on) => {
        if (on && matches()) {
            sync();
            start();
        } else {
            stop();
            reset();
        }
    });

    onUnmounted(() => {
        stop();
        reset();
    });
}
